import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiPlus, FiTrash2, FiSave, FiSearch, FiPackage, FiShoppingCart, 
  FiEdit3, FiX, FiActivity, FiHexagon, FiChevronDown, FiChevronUp, 
  FiTrendingUp, FiDollarSign, FiCheckCircle, FiAlertTriangle 
} = FiIcons;

const TABLES = {
  MATERIALS: 'rox_materials_1740635000',
  ITEMS: 'rox_items_1740635000',
  PACKS: 'rox_packs_1740635500'
};

export default function PackCalculator() {
  const [materials, setMaterials] = useState([]);
  const [items, setItems] = useState([]);
  const [packs, setPacks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [diamondRate, setDiamondRate] = useState(100);
  const [currentPackId, setCurrentPackId] = useState(null);
  const [packName, setPackName] = useState('');
  const [diamondCost, setDiamondCost] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPacks, setExpandedPacks] = useState({});

  useEffect(() => {
    fetchData();
    const savedRate = localStorage.getItem('rox_diamond_rate');
    if (savedRate) setDiamondRate(parseFloat(savedRate));
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    const [matsRes, itemsRes, packsRes] = await Promise.all([
      supabase.from(TABLES.MATERIALS).select('item_name, e_cost, category'),
      supabase.from(TABLES.ITEMS).select('item_name, e_cost, category'),
      supabase.from(TABLES.PACKS).select('*').order('created_at', { ascending: false })
    ]);

    if (matsRes.data) setMaterials(matsRes.data.map(m => ({ ...m, type: 'Material' })));
    if (itemsRes.data) setItems(itemsRes.data.map(i => ({ ...i, type: 'Item' })));
    if (packsRes.data) setPacks(packsRes.data);
    setIsLoading(false);
  };

  const handleRateChange = (val) => {
    const rate = parseFloat(val) || 0;
    setDiamondRate(rate);
    localStorage.setItem('rox_diamond_rate', rate.toString());
  };

  const allAvailable = useMemo(() => [...materials, ...items], [materials, items]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return [];
    return allAvailable.filter(item => 
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase())
    ).slice(0, 10);
  }, [searchQuery, allAvailable]);

  const totalMarketWorth = useMemo(() => {
    return selectedItems.reduce((acc, item) => acc + (item.e_cost * item.quantity), 0);
  }, [selectedItems]);

  const diamondValueInCrystals = useMemo(() => diamondCost * diamondRate, [diamondCost, diamondRate]);
  const savings = useMemo(() => totalMarketWorth - diamondValueInCrystals, [totalMarketWorth, diamondValueInCrystals]);
  const efficiency = useMemo(() => diamondValueInCrystals === 0 ? 0 : (totalMarketWorth / diamondValueInCrystals), [totalMarketWorth, diamondValueInCrystals]);
  const isWorthIt = useMemo(() => diamondValueInCrystals === 0 ? false : totalMarketWorth > (diamondValueInCrystals * 1.15), [totalMarketWorth, diamondValueInCrystals]);

  const addItemToPack = (item) => {
    const existing = selectedItems.find(i => i.item_name === item.item_name);
    if (existing) {
      setSelectedItems(selectedItems.map(i => i.item_name === item.item_name ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
    }
    setSearchQuery('');
  };

  const savePack = async () => {
    if (!packName || selectedItems.length === 0) return;
    const packData = {
      name: packName,
      items: selectedItems,
      total_e_cost: totalMarketWorth,
      diamond_cost: diamondCost
    };
    const { error } = currentPackId 
      ? await supabase.from(TABLES.PACKS).update(packData).eq('id', currentPackId)
      : await supabase.from(TABLES.PACKS).insert([packData]);
    if (!error) { resetForm(); fetchData(); }
  };

  const resetForm = () => {
    setCurrentPackId(null); setPackName(''); setDiamondCost(0); setSelectedItems([]); setSearchQuery('');
  };

  const editPack = (pack) => {
    setCurrentPackId(pack.id); setPackName(pack.name); setDiamondCost(pack.diamond_cost || 0); setSelectedItems(pack.items);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-12">
      {/* Header & Global Settings */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-2">
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">Pack Calculator</h1>
          <p className="text-gray-500">Calculate bundle value vs market exchange costs.</p>
        </div>
        <div className="bg-blue-50/50 p-4 rounded-3xl border border-blue-100 flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
            <SafeIcon icon={FiDollarSign} />
          </div>
          <div>
            <label className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Exchange Rate</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold">1 Diamond=</span>
              <input type="number" value={diamondRate} onChange={(e) => handleRateChange(e.target.value)} className="w-20 bg-transparent border-b border-blue-200 font-mono font-bold text-blue-700 outline-none text-center" />
              <span className="text-sm font-bold">Crystals</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Calculator Form */}
      <div className="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-xl shadow-gray-200/50 space-y-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Pack Name</label>
            <input type="text" value={packName} onChange={(e) => setPackName(e.target.value)} placeholder="e.g. Upgrade Pack" className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all font-medium" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Diamond Cost</label>
            <div className="relative">
              <input type="number" value={diamondCost} onChange={(e) => setDiamondCost(parseFloat(e.target.value) || 0)} className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all font-mono font-bold" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">💎</span>
            </div>
          </div>
          <div className="space-y-1 relative">
            <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Add Item</label>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full pl-11 pr-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:bg-white" />
            </div>
            <AnimatePresence>
              {searchQuery && (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-60 overflow-y-auto">
                  {filteredItems.map((item, idx) => (
                    <button key={idx} onClick={() => addItemToPack(item)} className="w-full px-5 py-3 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-50 last:border-0 transition-colors">
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{item.item_name}</p>
                        <p className="text-[10px] text-gray-400 uppercase font-black">{item.category}</p>
                      </div>
                      <span className="text-xs font-mono font-bold text-blue-600">{item.e_cost} C</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="space-y-3">
          {selectedItems.map((item, idx) => (
            <motion.div layout key={item.item_name} className="flex items-center justify-between bg-gray-50/50 p-4 rounded-2xl border border-gray-100 group">
              <div className="flex-1">
                <p className="font-bold text-gray-900">{item.item_name}</p>
                <p className="text-[10px] text-blue-600 font-bold uppercase">{item.e_cost} Crystal / unit</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center bg-white border border-gray-200 rounded-xl px-2">
                  <button onClick={() => setSelectedItems(selectedItems.map(i => i.item_name === item.item_name ? { ...i, quantity: Math.max(1, i.quantity - 1) } : i))} className="w-8 h-8 flex items-center justify-center font-bold text-gray-400 hover:text-black">-</button>
                  <input type="number" value={item.quantity} onChange={(e) => setSelectedItems(selectedItems.map(i => i.item_name === item.item_name ? { ...i, quantity: parseInt(e.target.value) || 1 } : i))} className="w-10 text-center font-bold outline-none bg-transparent" />
                  <button onClick={() => setSelectedItems(selectedItems.map(i => i.item_name === item.item_name ? { ...i, quantity: i.quantity + 1 } : i))} className="w-8 h-8 flex items-center justify-center font-bold text-gray-400 hover:text-black">+</button>
                </div>
                <div className="w-24 text-right">
                  <p className="font-mono font-black text-gray-900">{(item.e_cost * item.quantity).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelectedItems(selectedItems.filter(i => i.item_name !== item.item_name))} className="text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
              </div>
            </motion.div>
          ))}
          {selectedItems.length === 0 && (
            <div className="py-20 border-2 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center text-gray-300">
              <SafeIcon icon={FiPackage} className="text-4xl opacity-20 mb-2" />
              <p className="text-sm font-bold uppercase tracking-widest">Pack is empty</p>
            </div>
          )}
        </div>

        <div className="pt-8 border-t border-gray-100 grid md:grid-cols-4 gap-4">
          <div className="p-5 bg-gray-50 rounded-3xl border border-gray-100">
            <p className="text-[10px] font-black text-gray-400 uppercase mb-1 tracking-widest">Market Worth</p>
            <p className="text-2xl font-mono font-black">{totalMarketWorth.toLocaleString()} C</p>
          </div>
          <div className="p-5 bg-purple-50 rounded-3xl border border-purple-100">
            <p className="text-[10px] font-black text-purple-600 uppercase mb-1 tracking-widest">Diamond Value</p>
            <p className="text-2xl font-mono font-black text-purple-700">{diamondValueInCrystals.toLocaleString()} C</p>
          </div>
          <div className={`p-5 rounded-3xl border ${savings >= 0 ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
            <p className="text-[10px] font-black text-gray-500 uppercase mb-1 tracking-widest">Potential Profit</p>
            <p className={`text-2xl font-mono font-black ${savings >= 0 ? 'text-green-600' : 'text-red-600'}`}> {Math.abs(savings).toLocaleString()} C </p>
          </div>
          <div className="p-5 bg-blue-50 rounded-3xl border border-blue-100">
            <p className="text-[10px] font-black text-blue-600 uppercase mb-1 tracking-widest">Efficiency</p>
            <p className="text-2xl font-mono font-black text-blue-700">{efficiency.toFixed(2)}x</p>
          </div>
        </div>

        {selectedItems.length > 0 && diamondCost > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`p-6 rounded-3xl border flex items-center justify-between ${isWorthIt ? 'bg-green-600 text-white border-green-700' : 'bg-red-600 text-white border-red-700'} shadow-lg`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={isWorthIt ? FiCheckCircle : FiAlertTriangle} className="text-2xl" />
              </div>
              <div>
                <h3 className="text-xl font-black uppercase tracking-tight">{isWorthIt ? 'Worth It!' : 'Do Not Buy!'}</h3>
                <p className="text-sm opacity-80 font-medium">
                  {isWorthIt ? `This pack provides ${((efficiency - 1) * 100).toFixed(0)}% more value than raw diamonds.` : `This pack is only ${efficiency.toFixed(2)}x value. Wait for a better deal.`}
                </p>
              </div>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-[10px] font-black uppercase opacity-60">Value Threshold</p>
              <p className="font-bold">115% Required</p>
            </div>
          </motion.div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <button onClick={resetForm} className="px-6 py-3 text-sm font-bold text-gray-400 hover:text-black transition-colors">Reset</button>
          <button onClick={savePack} disabled={!packName || selectedItems.length === 0} className="px-10 py-3 bg-black text-white rounded-2xl text-sm font-bold shadow-xl shadow-black/10 disabled:opacity-20 hover:scale-[1.02] active:scale-[0.98] transition-all">
            {currentPackId ? 'Update Comparison' : 'Save Comparison'}
          </button>
        </div>
      </div>

      {/* History Section Moved Below */}
      <div className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <SafeIcon icon={FiTrendingUp} className="text-gray-400" /> Comparison History
          </h2>
          <span className="text-xs font-black bg-gray-100 px-3 py-1.5 rounded-full text-gray-500 uppercase tracking-widest">{packs.length} Saved Analysis</span>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packs.map(pack => {
            const packCrystals = (pack.diamond_cost || 0) * diamondRate;
            const packEfficiency = packCrystals > 0 ? (pack.total_e_cost / packCrystals) : 0;
            const packIsWorthIt = packEfficiency > 1.15;

            return (
              <div key={pack.id} className={`bg-white rounded-[2rem] border ${currentPackId === pack.id ? 'border-blue-500 ring-4 ring-blue-500/5' : 'border-gray-100'} shadow-sm hover:shadow-xl transition-all group overflow-hidden flex flex-col`}>
                <div className="p-6 pb-4 flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">{pack.name}</h4>
                      <p className="text-[10px] font-black text-gray-400 uppercase mt-1 tracking-tighter">
                        Cost: {pack.diamond_cost || 0} 💎 ({(pack.diamond_cost * diamondRate).toLocaleString()} C)
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => editPack(pack)} className="p-2 text-gray-300 hover:text-black"><SafeIcon icon={FiEdit3} /></button>
                      <button onClick={async () => { if(confirm('Delete?')) { await supabase.from(TABLES.PACKS).delete().eq('id', pack.id); fetchData(); } }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                    </div>
                  </div>

                  <div className="flex items-end justify-between pt-4 border-t border-gray-50">
                    <div>
                      <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Market Value</p>
                      <p className="font-mono font-black text-gray-900">{pack.total_e_cost?.toLocaleString()} C</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Efficiency</p>
                      <p className={`font-mono font-black ${packIsWorthIt ? 'text-green-600' : 'text-red-500'}`}>
                        {packEfficiency.toFixed(2)}x
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`px-6 py-3 text-[10px] font-black uppercase tracking-widest text-center border-t border-gray-50 flex items-center justify-center gap-2 ${packIsWorthIt ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <SafeIcon icon={packIsWorthIt ? FiCheckCircle : FiAlertTriangle} />
                  {packIsWorthIt ? 'Worth It!' : 'Do Not Buy!'}
                </div>

                <div className="px-6 py-4 bg-gray-50/50">
                  <button onClick={() => setExpandedPacks(prev => ({ ...prev, [pack.id]: !prev[pack.id] }))} className="w-full flex items-center justify-between text-[10px] font-black text-gray-400 hover:text-black uppercase tracking-widest">
                    {expandedPacks[pack.id] ? 'Hide Details' : `Show ${pack.items.length} Items`}
                    <SafeIcon icon={expandedPacks[pack.id] ? FiChevronUp : FiChevronDown} />
                  </button>
                  <AnimatePresence>
                    {expandedPacks[pack.id] && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-3 space-y-1.5">
                        {pack.items.map((it, i) => (
                          <div key={i} className="flex justify-between text-[11px] py-1 border-b border-gray-100 last:border-0">
                            <span className="text-gray-500">{it.quantity}x {it.item_name}</span>
                            <span className="font-mono font-bold">{(it.quantity * it.e_cost).toLocaleString()}</span>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}